import { computed, readonly, ref, shallowRef } from 'vue';
import { setCampuses } from './data/campuses';
import { setCategories } from './data/categories';
import { fetchCampuses, fetchCategories, fetchLocations } from './data/directus';
import { normalizeLocations } from './data/normalize';
import { buildSearchIndex } from './data/search';
import type { MapLocation } from './data/types';

// App-wide location data. UI state (selected location, open category, campus) lives in the
// URL so links are shareable and the browser Back button works; see router.ts.

type Status = 'idle' | 'loading' | 'ready' | 'error';

const status = ref<Status>('idle');
const error = ref<string | null>(null);
const locations = shallowRef<MapLocation[]>([]);

const bySlug = computed(() => new Map(locations.value.map((l) => [l.slug, l])));
const searchIndex = computed(() => buildSearchIndex(locations.value));
const countsByCategory = computed(() => {
  const counts = new Map<string, number>();
  for (const l of locations.value) counts.set(l.category, (counts.get(l.category) ?? 0) + 1);
  return counts;
});

let inFlight: Promise<void> | null = null;

export function loadLocations(): Promise<void> {
  if (inFlight) return inFlight;
  status.value = 'loading';
  error.value = null;
  // Campuses and categories are optional (built-in fallbacks); locations are required.
  const extras = Promise.all([fetchCampuses(), fetchCategories()]).then(([campusRows, categoryRows]) => {
    if (campusRows) setCampuses(campusRows);
    if (categoryRows) setCategories(categoryRows);
  });
  inFlight = Promise.all([fetchLocations(), extras])
    .then(([rows]) => {
      const result = normalizeLocations(rows);
      if (result.problems.length) {
        console.warn(`[maps] ${result.problems.length} location data problem(s):\n- ${result.problems.join('\n- ')}`);
      }
      locations.value = result.locations;
      status.value = 'ready';
    })
    .catch((err: unknown) => {
      console.error('[maps] failed to load locations', err);
      error.value = err instanceof Error ? err.message : String(err);
      status.value = 'error';
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

export function useLocations() {
  return {
    status: readonly(status),
    error: readonly(error),
    locations,
    bySlug,
    searchIndex,
    countsByCategory,
    load: loadLocations,
  };
}
